type FbqFn = ((...args: unknown[]) => void) & {
  queue?: unknown[]
  callMethod?: (...args: unknown[]) => void
  loaded?: boolean
}

declare global {
  interface Window {
    fbq?: FbqFn
  }
}

export const fbqTrackCustom = (
  eventName: string,
  eventData: Record<string, unknown> = {}
) => {
  if (typeof window === "undefined") return

  const fbq = window.fbq
  if (!fbq) {
    // The snippet defines fbq synchronously in <head>, so this means the
    // snippet is missing, not that fbevents.js is still loading.
    console.warn(`[pixel] ${eventName} dropped: window.fbq is undefined`)
    return
  }

  // `loaded` is set by the head snippet's stub and kept by fbevents.js; a
  // pending queue means fbevents.js has not drained it yet, so the event is
  // buffered rather than sent.
  if (import.meta.env.DEV) {
    console.log(`[pixel] ${eventName}`, {
      data: eventData,
      queued: fbq.queue?.length ?? null,
      hasCallMethod: !!fbq.callMethod,
    })
  }

  fbq("trackCustom", eventName, eventData)
}

/**
 * Web FICO band -> the 0-4 bucket mobile sends as `suitabilityScore`.
 *
 * MUST NOT be replaced with the raw band or a real score. Meta's policy forbids
 * sending credit data, so mobile deliberately reports a coarse proxy under a
 * neutral name (see backflip-mobile src/utils/PixelUtils.ts, which carries the
 * policy link and the same warning). Web's band values differ from mobile's
 * strings, so this is its own map, but the resulting buckets are identical.
 * https://www.facebook.com/business/help/361948878201809
 */
const FICO_BAND_TO_SUITABILITY: Record<string, number> = {
  lt640: 0,
  "640-669": 1,
  "670-689": 2,
  "690-749": 3,
  "750plus": 4,
}

/** The subset of the intake form the pixel is allowed to see. */
export interface LoanApplicationPixelValues {
  borrowerType?: string
  ficoBand?: string
  flips?: number
  rentals?: number
  cashAvailable?: string
  purchaseIntent?: string
}

/**
 * Build the `LAppSubmitted` payload, mirroring mobile's `fbqUserAttributes` so
 * web and mobile share one Meta audience definition. Deal figures (purchase
 * price, ARV, rehab budget, address) are deliberately NOT included — property
 * financials should not go to an ad platform. Undefined fields are dropped.
 */
export const buildLoanApplicationPixelAttributes = (
  values: LoanApplicationPixelValues
): Record<string, unknown> => {
  const attributes: Record<string, unknown> = {}

  if (values.flips !== undefined) attributes.propertiesFlipped = values.flips
  if (values.rentals !== undefined) attributes.rentalsOwned = values.rentals
  if (values.cashAvailable !== undefined) {
    attributes.cashAvailable = values.cashAvailable
  }
  if (values.borrowerType !== undefined) {
    attributes.borrower = values.borrowerType
  }
  // Mobile's `intentStatus` came from the pre-qual flow, which web does not
  // have; the intake's purchase intent is the closest equivalent signal.
  if (values.purchaseIntent !== undefined) {
    attributes.intentStatus = values.purchaseIntent
  }

  if (values.ficoBand !== undefined) {
    const bucket = FICO_BAND_TO_SUITABILITY[values.ficoBand]
    if (bucket !== undefined) {
      attributes.suitabilityScore = bucket
      // Mobile's flag: whether the borrower clears the lowest band.
      attributes.is_qualified = values.ficoBand !== "lt640"
    }
  }

  return attributes
}
